/**
 * The 422 a placement returns when the session references files the temp bucket has retired
 * (ISSUE-3459). Both purchase flows receive it; shape is the backend's `MissingSessionFilesDTO`.
 */

import { CommonStatusCode } from 'constants/API'

/** Mirrors the backend's `SessionFileKind`. */
export enum SessionFileKind {
  VISUAL = 'VISUAL',
  DOCUMENT = 'DOCUMENT',
}

/** Exactly one of `productCode` / `orderDocumentType` is set; `productCode` compares by string equality. */
export interface MissingSessionFile {
  filename: string
  kind: SessionFileKind
  productCode: string | null
  orderDocumentType: string | null
}

export interface MissingSessionFiles {
  sessionId: string
  files: MissingSessionFile[]
}

const isMissingSessionFile = (value: unknown): value is MissingSessionFile => {
  if (typeof value !== 'object' || value === null) return false
  const file = value as Record<string, unknown>
  return typeof file.filename === 'string' && file.filename.length > 0
}

/**
 * The refusal carried by a failed placement, or `null` for every other failure. Narrowed on the body's
 * shape as well as the status: 422 is generic here, and other refusals use it too.
 */
export const parseMissingSessionFiles = (error: unknown): MissingSessionFiles | null => {
  if (typeof error !== 'object' || error === null) return null

  const response = (error as { response?: { status?: number, data?: unknown } }).response
  if (!response || response.status !== CommonStatusCode.UNPROCESSABLE_ENTITY) return null

  const data = response.data
  if (typeof data !== 'object' || data === null) return null

  const { sessionId, files } = data as { sessionId?: unknown, files?: unknown }
  if (typeof sessionId !== 'string') return null
  if (!Array.isArray(files) || files.length === 0) return null
  if (!files.every(isMissingSessionFile)) return null

  return {
    sessionId,
    files: files.map((file) => ({
      filename: file.filename,
      kind: file.kind === SessionFileKind.VISUAL ? SessionFileKind.VISUAL : SessionFileKind.DOCUMENT,
      productCode: typeof file.productCode === 'string' ? file.productCode : null,
      orderDocumentType: typeof file.orderDocumentType === 'string' ? file.orderDocumentType : null,
    })),
  }
}

/** The filenames as one list, for a message. */
export const missingFileNames = (missing: MissingSessionFiles): string =>
  missing.files.map((file) => file.filename).join(', ')
